import { addDays, format, parseISO, startOfWeek, endOfWeek, isWithinInterval, isValid } from 'date-fns';
import type { Locale } from 'date-fns';
import { enUS, ru } from 'date-fns/locale';
import type { LocaleCode, Plan } from './types';

/** Calendar date string YYYY-MM-DD — timezone-safe local date helpers */

export function toDateKey(date: Date): string {
  return format(date, 'yyyy-MM-dd');
}

export function parseDateKey(key: string): Date {
  const d = parseISO(`${key}T12:00:00`);
  if (!isValid(d)) {
    throw new Error(`Invalid date key: ${key}`);
  }
  return d;
}

export function todayKey(now = new Date()): string {
  return toDateKey(now);
}

export function yearMonthKey(date: Date): string {
  return format(date, 'yyyy-MM');
}

export function getDateFnsLocale(locale: LocaleCode): Locale {
  return locale === 'ru' ? ru : enUS;
}

export function getWeekStartsOn(locale: LocaleCode): 0 | 1 {
  return locale === 'ru' ? 1 : 0;
}

export function shiftDateKey(key: string, days: number): string {
  return toDateKey(addDays(parseDateKey(key), days));
}

export function isDateInWeek(dateKey: string, weekAnchor: Date, weekStartsOn: 0 | 1): boolean {
  const start = startOfWeek(weekAnchor, { weekStartsOn });
  const end = endOfWeek(weekAnchor, { weekStartsOn });
  const d = parseDateKey(dateKey);
  return isWithinInterval(d, { start, end });
}

export function compareTime(a: string | null, b: string | null): number {
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  return a.localeCompare(b);
}

export function sortPlansChronologically(plans: Plan[]): Plan[] {
  return [...plans].sort((a, b) => {
    const dateCmp = (a.planDate ?? '').localeCompare(b.planDate ?? '');
    if (dateCmp !== 0) return dateCmp;
    const timeCmp = compareTime(a.startTime, b.startTime);
    if (timeCmp !== 0) return timeCmp;
    return a.title.localeCompare(b.title);
  });
}

export function formatTimeDisplay(
  time: string | null,
  timeFormat: '12h' | '24h',
): string {
  if (!time) return '';
  const [hStr, mStr] = time.split(':');
  const h = Number(hStr);
  const m = Number(mStr);
  if (Number.isNaN(h) || Number.isNaN(m)) return time;
  if (timeFormat === '24h') {
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  }
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, '0')} ${period}`;
}

export function defaultTimeFormat(locale: LocaleCode): '12h' | '24h' {
  return locale === 'ru' ? '24h' : '12h';
}
